// src/api/clientApi.ts
import { api } from "@/services/api";

export interface Client {
  id: number;
  companyName: string;
  ein?: string;
  contactEmail: string;
  contactPhone?: string;
  businessAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  status: string;
  marginStrategy?: string;
  pocName?: string;
}

export interface ClientRequest {
  companyName: string;
  ein?: string;
  contactEmail: string;
  contactPhone?: string;
  businessAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  marginStrategy?: string;
  pocName?: string;
}

export interface Employee {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  contactPhone?: string;
  status: string;
  phoneNumber?: string;
}

export const clientApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getClients: builder.query<Client[], void>({
      query: () => "/super-admin/clients",
      providesTags: ["Clients"],
    }),
    getClientById: builder.query<Client, number>({
      query: (id) => `/super-admin/clients/${id}`,
      providesTags: ["Clients"],
    }),
    createClient: builder.mutation<Client, ClientRequest>({
      query: (body) => ({
        url: "/super-admin/clients",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Clients"],
    }),
    updateClient: builder.mutation<Client, { id: number; data: ClientRequest }>({
      query: ({ id, data }) => ({
        url: `/super-admin/clients/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Clients"],
    }),
    deactivateClient: builder.mutation<Client, number>({
      query: (id) => ({
        url: `/super-admin/clients/${id}/deactivate`,
        method: "PATCH",
      }),
      invalidatesTags: ["Clients", "Employees", "Users"],
    }),
    activateClient: builder.mutation<Client, number>({
      query: (id) => ({
        url: `/super-admin/clients/${id}/activate`,
        method: "PATCH",
      }),
      invalidatesTags: ["Clients", "Employees", "Users"],
    }),
    resetClientPassword: builder.mutation<void, { id: number; password: string }>({
      query: ({ id, password }) => ({
        url: `/super-admin/clients/${id}/reset-password`,
        method: "POST",
        body: { password },
      }),
    }),
    getClientUsers: builder.query<Employee[], number>({
      query: (clientId) => `/clients/${clientId}/employees`,
      providesTags: ["Employees"],
    }),
    createClientUser: builder.mutation<
      Employee,
      {
        clientId: number;
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber?: string;
        password: string;
        dateOfBirth?: string;
        gender?: string;
        role: string;
      }
    >({
      query: ({ clientId, firstName, lastName, email, phoneNumber, password, dateOfBirth, gender, role }) => {
        const body: any = {
          firstName,
          lastName,
          email,
          password,
          role
        };

        if (phoneNumber) body.phoneNumber = phoneNumber;
        if (dateOfBirth) {
          // Backend expects LocalDate (YYYY-MM-DD)
          body.dateOfBirth = dateOfBirth;
        }
        if (gender) body.gender = gender;

        return {
          url: `/clients/${clientId}/employees`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Employees"],
    }),
    updateClientUser: builder.mutation<
      Employee,
      {
        clientId: number;
        userId: number;
        firstName?: string;
        lastName?: string;
        email?: string;
        phoneNumber?: string;
        dateOfBirth?: string;
        gender?: string;
      }
    >({
      query: ({ clientId, userId, ...data }) => ({
        url: `/clients/${clientId}/employees/${userId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Employees"],
    }),
    resetClientEmployeePassword: builder.mutation<void, { clientId: number; userId: number; password: string }>({
      query: ({ clientId, userId, password }) => ({
        url: `/clients/${clientId}/employees/${userId}/reset-password`,
        method: "POST",
        body: { password },
      }),
    }),
  }),
});

export const {
  useGetClientsQuery,
  useGetClientByIdQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeactivateClientMutation,
  useActivateClientMutation,
  useResetClientPasswordMutation,
  useGetClientUsersQuery,
  useCreateClientUserMutation,
  useUpdateClientUserMutation,
  useResetClientEmployeePasswordMutation,
} = clientApi;
